import { Component, HostListener, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { NotificationsService } from '../../Services/shared/notifications.service';
import { AuthService } from '../../Services/auth.service';
import { NotificationTypes } from '../../app.enums';
import { ForgotPasswordMailRequest, UsersService } from 'src/app/Services/users.service';
import { isPlatformBrowser } from '@angular/common';
import { TranslocoService } from '@jsverse/transloco';
import { AppSettings } from '../../app.settings';

@Component({
    selector: 'app-auth',
    templateUrl: './auth.component.html',
    styleUrls: ['./auth.component.css'],
    standalone: false
})
export class AuthComponent implements OnInit {
  loginModel: LoginModel = {};
  public formData: ForgotPasswordMailRequest = {} as ForgotPasswordMailRequest;
  public showForgotPasswordDialog: boolean = false;
  public showPassword: boolean = false;
  public rememberMe: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private ngxSpinnerService: NgxSpinnerService,
    private notificationsService: NotificationsService,
    private authService: AuthService,
    private userService: UsersService,
    private transloco: TranslocoService
  ) { }
  public ngOnInit() {
    // Check if the user is already authenticated - redirect them away from login
    if (this.authService.isAuthenticated()) {
      if (this.authService.isAdministrator() || this.authService.IsSuperAdmin()) {
        this.router.navigate(['/ds-admin-dashboard']);
        return;
      }
      const authConfig = this.authService.getAuthConfig();
      if (authConfig.IsArtist) {
        const isEventArtist = this.authService.isEventArtist();
        this.router.navigate([isEventArtist ? '/dashboard' : '/ds-dashboard']);
        return;
      } else if (authConfig.IsJudge) {
        this.router.navigate(['/judge/dashboard']);
        return;
      } else if (authConfig.IsCurator) {
        this.router.navigate(['/curator/dashboard']);
        return;
      } else if (authConfig.IsGallery || authConfig.IsGalleryStaff) {
        this.router.navigate(['/ds-gallery-dashboard']);
        return;
      } else {
        this.router.navigate(['/dashboard']);
        return;
      }
    }


    // Not authenticated — prefill the username if the user asked us to remember it.
    this.loadRememberedUsername();
  }

public login(): void {
  this.persistRememberedUsername();
  this.ngxSpinnerService.show();
  this.authService.login(this.loginModel).subscribe(
    (response) => {
      console.log(response);
      this.ngxSpinnerService.hide();

      if (response.Success && response.ResponseData) {
        const authUser = response.ResponseData.AuthUser;
        const completedSteps = authUser.CompletedUserOnboardingSteps ?? 0;
        const hideChecklist = authUser.HideUserOnBoardingCheckList ?? false;
        const authConfig = this.authService.getAuthConfig();

        if (authConfig.IsArtist) {
          // Check if event-only artist - they use old dashboard
          // Check directly from authUser response (more reliable than token decode)
          const isEventArtist = authUser.GalleryEventId ? true : false;

          if (completedSteps < OnboardingStepsEnum.addFirstArtwork) {
            // Steps incomplete
            if (!hideChecklist) {
              // Show onboarding checklist
              this.router.navigate(['/artist-onboarding-checklist']).then(() => this.safeReload());
            } else {
              // Steps incomplete but hide checklist flag is true
              // Event artists go to old dashboard, regular artists go to ds-dashboard
              const redirectPath = isEventArtist ? '/dashboard' : '/ds-dashboard';
              this.router.navigate([redirectPath]).then(() => this.safeReload());
            }
          } else {
            // Steps complete - Event artists go to old dashboard, regular artists go to ds-dashboard
            const redirectPath = isEventArtist ? '/dashboard' : '/ds-dashboard';
            this.router.navigate([redirectPath]).then(() => this.safeReload());
          }
        } else if (authConfig.IsJudge) {
          this.router.navigate(['/judge/dashboard']).then(() => this.safeReload());
        } else if (authConfig.IsCurator) {
          this.router.navigate(['/curator/dashboard']).then(() => this.safeReload());
        } else if (authConfig.IsGallery || authConfig.IsGalleryStaff) {
          this.router.navigate(['/ds-gallery-dashboard']).then(() => this.safeReload());
        } else if (this.authService.isAdministrator() || this.authService.IsSuperAdmin()) {
          this.router.navigate(['/ds-admin-dashboard']).then(() => this.safeReload());
        } else {
          this.router.navigate(['/dashboard']).then(() => this.safeReload());
        }

        return;
      }

      // If response is not successful
      this.notificationsService.showNotification(this.transloco.translate('auth.errorTitle'), response.ResponseMessage, NotificationTypes.Error);
    },
    (error) => {
      console.error(error);
      this.ngxSpinnerService.hide();
      // Surface HTTP errors (e.g. 429 throttling, 5xx, network). The API returns a
      // ResponseObject envelope in the body for handled cases (incl. 429); fall back
      // to a generic message when there is no body (network failure).
      const message = error?.error?.ResponseMessage || this.transloco.translate('auth.requestFailed');
      this.notificationsService.showNotification(this.transloco.translate('auth.errorTitle'), message, NotificationTypes.Error);
    }
  );
}

/**
 * Safe reload that only works in browser environment
 */
private safeReload(): void {
  if (isPlatformBrowser(this.platformId)) {
    window.location.reload();
  }
}

/**
 * Prefill the username from a previous "Remember me" choice. We only ever store
 * the username — never the password (the browser's own password manager handles
 * that via the autocomplete attributes on the form).
 */
private loadRememberedUsername(): void {
  if (!isPlatformBrowser(this.platformId)) return;
  try {
    const saved = localStorage.getItem(AppSettings.REMEMBERED_USERNAME);
    if (saved) {
      this.loginModel.Username = saved;
      this.rememberMe = true;
    }
  } catch { /* storage unavailable / blocked — ignore */ }
}

/** Persist (or clear) the remembered username based on the checkbox. Username only. */
private persistRememberedUsername(): void {
  if (!isPlatformBrowser(this.platformId)) return;
  try {
    if (this.rememberMe && this.loginModel.Username) {
      localStorage.setItem(AppSettings.REMEMBERED_USERNAME, this.loginModel.Username);
    } else {
      localStorage.removeItem(AppSettings.REMEMBERED_USERNAME);
    }
  } catch { /* storage unavailable / blocked — ignore */ }
}





 

  public onForgotPasswordDialog() {
    this.showForgotPasswordDialog = true;
  }

  public closeDialog() {
    this.showForgotPasswordDialog = false;
    this.formData = new ForgotPasswordMailRequest();
  }

  public SendForgotPasswordMail() {
    this.ngxSpinnerService.show();
    this.userService.sendForgotPasswordMail(this.formData).subscribe(
      (response) => {
        this.ngxSpinnerService.hide();
        if (response.Success) {
          this.showForgotPasswordDialog = false;
          this.formData = new ForgotPasswordMailRequest();
          this.notificationsService.showNotification(this.transloco.translate('auth.successTitle'), this.transloco.translate('auth.forgotPasswordSent'), NotificationTypes.Success);
        }
        else {
          console.error(response);
          this.notificationsService.showNotification(this.transloco.translate('auth.errorTitle'), this.transloco.translate('auth.forgotPasswordError'), NotificationTypes.Error);
        }
      },
      (error) => {
        console.error(error);
        this.ngxSpinnerService.hide();
      }
    );
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    // Only handle keyboard events in browser
    if (!isPlatformBrowser(this.platformId)) return;

    // The login form submits natively via (ngSubmit), so Enter is handled there.
    // Here we only need to cover the forgot-password dialog.
    if (event.key === 'Enter' && this.showForgotPasswordDialog) {
      this.SendForgotPasswordMail();
    }
  }

}

export interface LoginModel {
  Username?: string;
  Password?: string;
}
export enum OnboardingStepsEnum {
  primaryArtType = 1,
  additionalTypes,
  updateUserDetails,
  artistStatementResume,
  setupArtworkSeries,
  preferredMaterials,
  marketingFeatures,
  addFirstArtwork
}